import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Search, Star, User, Plus } from "lucide-react";
import { toast } from "sonner";
import { brandify } from "../lib/brand";

const STAT_TONE = {
  "StaffHub Approved":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "Available":"bg-sky-50 text-sky-700 border-sky-200",
  "Submitted":"bg-indigo-50 text-indigo-700 border-indigo-200",
  "Interviewing":"bg-amber-50 text-amber-700 border-amber-200",
  "Placed":"bg-purple-50 text-purple-700 border-purple-200",
  "Screening":"bg-slate-100 text-slate-700 border-slate-200",
  "New":"bg-slate-100 text-slate-500 border-slate-200",
  "Applied":"bg-slate-100 text-slate-700 border-slate-200",
  "Rejected":"bg-red-50 text-red-700 border-red-200",
};

export default function Candidates() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [role, setRole] = useState("");
  const [minScore, setMinScore] = useState("");
  const [meta, setMeta] = useState({});
  const [activeCountries, setActiveCountries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({first_name:"",last_name:"",email:"",primary_role:"",country:"",years_experience:0,english_spoken:3,english_written:3});
  const [sel, setSel] = useState(null);

  useEffect(() => {
    api.get("/meta/lookups").then(r=>setMeta(r.data));
    // Dynamic country filter — only countries with current talent
    api.get("/geography/countries/active").then(r=>setActiveCountries(r.data.countries || []));
  }, []);
  const load = async () => {
    const p = {};
    if (q) p.q = q; if (country) p.country = country; if (role) p.role = role; if (minScore) p.min_score = minScore;
    const { data } = await api.get("/candidates", { params: p });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, country, role, minScore]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/candidates", form);
      toast.success("Candidate added");
      setOpen(false); setForm({first_name:"",last_name:"",email:"",primary_role:"",country:"",years_experience:0,english_spoken:3,english_written:3});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Talent Network</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">ConnectMyHub Talent Network</h1>
          <p className="text-sm text-slate-500 mt-1">Vetted U.S. and global talent across industries, roles, locations, and workforce models.</p>
        </div>
        <button data-testid="new-candidate-btn" onClick={()=>setOpen(true)} className="h-10 px-4 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold inline-flex items-center gap-2"><Plus size={15}/> Add candidate</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input data-testid="candidates-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search name, headline, skills…" className="w-full pl-9 pr-3 h-9 rounded-md border border-slate-200 text-sm bg-white outline-none focus:border-sky-500"/>
          </div>
          <select data-testid="filter-country" value={country} onChange={e=>setCountry(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">All countries ({activeCountries.length})</option>
            {activeCountries.map(c=><option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
          </select>
          <select data-testid="filter-role" value={role} onChange={e=>setRole(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">All roles</option>
            {(meta.roles||[]).map(c=><option key={c}>{c}</option>)}
          </select>
          <select data-testid="filter-score" value={minScore} onChange={e=>setMinScore(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">Any score</option>
            <option value="3">≥ 3.0</option><option value="4">≥ 4.0</option><option value="4.5">≥ 4.5</option>
          </select>
          <div className="text-xs text-slate-500 ml-auto">{rows.length} candidates</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Candidate</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Country</th>
                <th className="text-left px-4 py-3">English</th>
                <th className="text-left px-4 py-3">Comp/mo</th>
                <th className="text-left px-4 py-3">ConnectMyHub Score</th>
                <th className="text-left px-4 py-3">Stage</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} data-testid={`candidate-row-${r.id}`} onClick={()=>nav(`/candidates/${r.id}`)} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center border border-sky-100"><User size={13}/></div>
                      <div>
                        <div className="font-semibold text-slate-900">{r.first_name} {r.last_name}</div>
                        <div className="text-[11px] text-slate-500 truncate max-w-[280px]">{r.headline}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.primary_role}</td>
                  <td className="px-4 py-3 text-slate-600">{r.country}</td>
                  <td className="px-4 py-3"><div className="font-mono text-xs">W{r.english_written}/S{r.english_spoken}</div></td>
                  <td className="px-4 py-3 font-mono text-slate-700">${r.desired_compensation?.toLocaleString() || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1.5 font-mono text-slate-900 font-semibold"><Star size={13} className="text-amber-500 fill-amber-500"/>{r.overall_staffhub_score?.toFixed(1)}</div>
                  </td>
                  <td className="px-4 py-3 text-[11px] text-slate-600">{r.pipeline_stage}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded border ${STAT_TONE[r.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.status}</span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-sm text-slate-500">No candidates yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {sel && (
        <div className="fixed inset-0 z-40 bg-slate-900/50 flex justify-end" onClick={()=>setSel(null)}>
          <div onClick={e=>e.stopPropagation()} data-testid="candidate-drawer" className="w-full max-w-lg bg-white h-full overflow-y-auto p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center"><User size={20}/></div>
              <div>
                <h3 className="font-display font-bold text-xl text-slate-900">{sel.first_name} {sel.last_name}</h3>
                <p className="text-xs text-slate-500">{sel.headline}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs mb-6">
              <Row k="Country" v={sel.country}/> <Row k="Time zone" v={sel.time_zone}/>
              <Row k="Years exp" v={sel.years_experience}/> <Row k="Primary role" v={sel.primary_role}/>
              <Row k="English W/S" v={`${sel.english_written}/${sel.english_spoken}`}/> <Row k="Comp/mo" v={`$${sel.desired_compensation}`}/>
              <Row k="Available" v={sel.earliest_start}/> <Row k="Source" v={sel.source}/>
            </div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Vetting scorecard (1–5)</h4>
            <div className="grid grid-cols-2 gap-2 text-xs mb-4">
              {[["Communication","score_communication"],["Confidence","score_confidence"],["Experience","score_experience"],["Software","score_software"],["AI proficiency","score_ai"],["Time-zone fit","score_timezone"],["Reliability","score_reliability"],["Role skills","score_role_skills"]].map(([l,k]) => (
                <div key={k} className="flex justify-between px-2 py-1.5 bg-slate-50 rounded border border-slate-100">
                  <span className="text-slate-600">{l}</span>
                  <span className="font-mono font-semibold">{sel[k] || 0}/5</span>
                </div>
              ))}
            </div>
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 mb-4">
              <span className="font-semibold">Overall ConnectMyHub Score: </span>{sel.overall_staffhub_score?.toFixed(2)} / 5.00
            </div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Skills</h4>
            <div className="flex flex-wrap gap-1.5 mb-4">{(sel.skills||[]).map(s => <span key={s} className="text-[11px] px-2 py-0.5 rounded border border-slate-200 bg-slate-50">{s}</span>)}</div>
            {sel.recruiter_notes && <><h4 className="text-sm font-semibold text-slate-900 mb-2">Recruiter notes</h4><p className="text-xs text-slate-600">{sel.recruiter_notes}</p></>}
            <button onClick={()=>setSel(null)} className="mt-6 w-full h-9 rounded-md border border-slate-300 text-sm">Close</button>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-6" onClick={()=>setOpen(false)}>
          <form onClick={e=>e.stopPropagation()} onSubmit={submit} data-testid="new-candidate-form" className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="font-display font-bold text-xl text-slate-900 mb-4">Add candidate</h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["first_name","First name",true],["last_name","Last name",true],
                ["email","Email"],["primary_role","Primary role"],["country","Country"],
              ].map(([k,l,req]) => (
                <div key={k}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">{l}</label>
                  <input data-testid={`cand-field-${k}`} required={req} value={form[k]||""} onChange={e=>setForm({...form,[k]:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm outline-none focus:border-sky-500"/>
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Years experience</label>
                <input type="number" min="0" value={form.years_experience} onChange={e=>setForm({...form,years_experience:parseInt(e.target.value||0)})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">English spoken (1-5)</label>
                <input type="number" min="1" max="5" value={form.english_spoken} onChange={e=>setForm({...form,english_spoken:parseInt(e.target.value||0)})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm"/>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>setOpen(false)} className="h-9 px-4 rounded-md border border-slate-300 text-sm">Cancel</button>
              <button data-testid="cand-submit" type="submit" className="h-9 px-4 rounded-md bg-slate-900 text-white text-sm font-semibold">Add</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
const Row = ({k,v}) => (<><div className="text-slate-500 uppercase tracking-widest text-[10px]">{k}</div><div className="text-slate-900 font-medium">{v || "—"}</div></>);
